import { z } from 'zod';
import { orderAddress } from '../../schema/address.js';
import { Pegasus } from '../../schema/pegasus.js';
const money = z.object({
    amount: z.string(),
    currencyCode: z.string(),
});
// a list of actions from pegasus that can be performed on an order
export const orderEditStartResponse = z.array(Pegasus.orderEditAction);
const cancel = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.CANCEL_ORDER}`),
    action: Pegasus.orderEditCancelOrderActionSchema,
    stagedEdits: z.never().optional(),
});
const updateShippingAddress = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
    action: Pegasus.orderEditUpdateAddressActionSchema,
    stagedEdits: z.object({
        updatedShippingAddress: orderAddress,
        // present only when shouldRecalculateTaxesOnAddressEdit ran at stage
        originalTotal: money.optional(),
        calculatedTotal: money.optional(),
        additionalPaymentCollectionUrl: z.url().nullish(),
    }),
});
const removePackageProtection = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION}`),
    action: Pegasus.orderEditRemovePackageProtection,
    stagedEdits: z.never().optional(),
});
const modifyQuantity = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.MODIFY_QUANTITY}`),
    action: Pegasus.orderEditModifyQuantityActionSchema,
    stagedEdits: z.object({
        originalTotal: money,
        calculatedTotal: money,
        additionalPaymentCollectionUrl: z.url().nullable(),
        lineItemDeltas: z.array(z.object({
            storeOrderLineItemId: z.number(),
            newQuantity: z.number().int().nonnegative(),
            previousQuantity: z.number().int().nonnegative(),
        })),
    }),
});
const updateEmail = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.UPDATE_EMAIL}`),
    action: Pegasus.orderEditUpdateEmailActionSchema,
    stagedEdits: z.object({
        email: z.email(),
    }),
});
const updateShippingLine = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.UPDATE_SHIPPING_LINE}`),
    action: Pegasus.orderEditUpdateShippingLineActionSchema,
    stagedEdits: z.object({
        selectedShippingLine: z.object({
            title: z.string(),
            price: z.string(),
        }),
        previousShippingLine: z.object({
            title: z.string(),
            price: z.string(),
        }),
        originalTotal: money,
        calculatedTotal: money,
        additionalPaymentCollectionUrl: z.url().nullable(),
    }),
});
const addDiscountCode = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.ADD_DISCOUNT_CODE}`),
    action: Pegasus.orderEditAddDiscountCodeActionSchema,
    stagedEdits: z.object({
        code: z.string().trim().min(1).max(64),
        originalTotal: money,
        calculatedTotal: money,
        refundAmount: money,
    }),
});
// ! Order matters in the discriminated union arrays, as the frontend selects based on an index!
// used for both the staging response and commit request
export const orderEditCommitOrStage = z.discriminatedUnion('editType', [
    cancel,
    updateShippingAddress,
    removePackageProtection,
    modifyQuantity,
    updateEmail,
    updateShippingLine,
    addDiscountCode,
]);
const extendSchema = {
    appliedOrderEditId: z.number(),
    ok: z.literal(true),
};
// ! Order matters in the discriminated union arrays, as the frontend selects based on an index!
export const orderEditStageResponse = z.discriminatedUnion('editType', [
    cancel.extend(extendSchema),
    updateShippingAddress.extend(extendSchema),
    removePackageProtection.extend(extendSchema),
    modifyQuantity.extend(extendSchema),
    updateEmail.extend(extendSchema),
    updateShippingLine.extend(extendSchema),
    addDiscountCode.extend(extendSchema),
]);
export const orderEditStageFailed = z.object({
    ok: z.literal(false),
    message: z.string(),
});
export const orderEditStageResponseUnion = z.union([
    orderEditStageResponse,
    orderEditStageFailed,
]);
const baseResponse = z.object({
    ok: z.boolean(),
    message: z.string(),
});
export const refundMethodEnum = z.enum(['STORE_CREDIT', 'ORIGINAL_PAYMENT']);
// ! Order matters in the discriminated union arrays, as the frontend selects based on an index!
export const orderEditCommitResponse = z.discriminatedUnion('editType', [
    baseResponse.extend({
        editType: z.literal(`${Pegasus.OrderEditType.CANCEL_ORDER}`),
        refundMethod: refundMethodEnum.nullish(),
    }),
    baseResponse.extend({
        editType: z.literal(`${Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS}`),
        additionalPaymentCollectionUrl: z.url().nullish(),
    }),
    baseResponse.extend({
        editType: z.literal(`${Pegasus.OrderEditType.REMOVE_PACKAGE_PROTECTION}`),
        refundedAmount: money.nullish(),
        refundMethod: refundMethodEnum.nullish(),
    }),
    baseResponse.extend({
        editType: z.literal(`${Pegasus.OrderEditType.MODIFY_QUANTITY}`),
        additionalPaymentCollectionUrl: z.url().nullish(),
        refundedAmount: money.nullish(),
        refundMethod: refundMethodEnum.nullish(),
    }),
    baseResponse.extend({
        editType: z.literal(`${Pegasus.OrderEditType.UPDATE_EMAIL}`),
    }),
    baseResponse.extend({
        editType: z.literal(`${Pegasus.OrderEditType.UPDATE_SHIPPING_LINE}`),
        additionalPaymentCollectionUrl: z.url().nullish(),
        refundedAmount: money.nullish(),
        refundMethod: refundMethodEnum.nullish(),
    }),
    baseResponse.extend({
        editType: z.literal(`${Pegasus.OrderEditType.ADD_DISCOUNT_CODE}`),
        refundedAmount: money.nullish(),
        refundMethod: refundMethodEnum.nullish(),
    }),
]);
// ---------- Prepare phase (opt-in per editType) ----------
// Only editTypes that need backend data before staging appear in these
// unions. To add a new opt-in type, add a request+response variant below.
const updateShippingLinePrepareRequest = z.object({
    editType: z.literal(`${Pegasus.OrderEditType.UPDATE_SHIPPING_LINE}`),
    action: Pegasus.orderEditUpdateShippingLineActionSchema,
});
const updateShippingLinePrepareResponse = updateShippingLinePrepareRequest.extend({
    prepared: z.object({
        quotes: z.array(z.object({
            title: z.string(),
            price: money,
            deltaFromCurrent: z.string(),
            newShippingTotal: money,
        })),
    }),
});
// ! Order matters in the discriminated union arrays, as the frontend selects based on an index!
export const orderEditPrepareRequest = z.discriminatedUnion('editType', [
    updateShippingLinePrepareRequest,
]);
export const orderEditPrepareResponse = z.discriminatedUnion('editType', [
    updateShippingLinePrepareResponse,
]);
//# sourceMappingURL=orderEdit.js.map